import toast from 'react-hot-toast';
import { BLACK_THEMED_PAGES } from 'constants/black-themed-pages';
import Cookies from 'js-cookie';
import queryString from 'query-string';

export const scrollIntoView = (destination, callback, additionalOffset = 0) => {
    if (typeof window !== 'undefined') {
        // eslint-disable-next-line unicorn/prefer-query-selector
        const offset = document.getElementById(destination).offsetTop;
        const finalOffset = offset + 100 + additionalOffset;
        const onScroll = () => {
            const scrollTop = window.scrollTop || window.pageYOffset;

            if (scrollTop === finalOffset) {
                window.removeEventListener('scroll', onScroll);
                if (callback) {
                    callback();
                }
            }
        };

        window.addEventListener('scroll', onScroll);
        onScroll();
        window.scrollTo({
            behavior: 'smooth',
            left: 0,
            top: finalOffset,
        });
    }
};

export const generateKebabCaseId = (value) => {
    return value
        .replace(/([a-z])([A-Z])/g, '$1-$2') // Separates camelCase words
        .replace(/\./g, '-') // Replaces dots with hyphens
        .toLowerCase() // Converts to lowercase
        .trim() // Removes whitespace from the beginning and end
        .replace(/[^\s\w-]/g, '') // Removes special characters (except hyphens and spaces)
        .replace(/\s+/g, '-') // Replaces spaces with hyphens
        .replace(/-+/g, '-') // Replaces multiple hyphens with a single hyphen
        .replace(/^-+|-+$/g, ''); // Removes hyphens from the beginning and end
};

export const getPageUrl = () => {
    if (typeof window !== 'undefined') {
        return window.location.pathname;
    }
    return '/';
};

export const pushToDataLayer = (eventName, eventData = {}) => {
    if (typeof window !== 'undefined' && window.dataLayer) {
        window.dataLayer.push({
            event: eventName,
            ...eventData,
        });
    }
};

// Click tracking: one GA4 `cta_click` per click on any link or button.
// A capture-phase listener describes the clicked element (text, destination,
// section); if a component names the click with `trackCustomEvent` during the
// same dispatch, the name joins that description, otherwise the click goes out
// unnamed right after. Every key is pushed on every call so a value from a
// previous click cannot leak into this one through GTM's merged data model.
const CLICKABLE =
    'a[href], button, [role="button"], [role="tab"], summary, input[type="submit"]';
const IGNORED_SECTION_IDS = new Set(['___gatsby', 'gatsby-focus-wrapper']);
let pendingClick = null;

const cleanText = (value) =>
    (value || '').replace(/\s+/g, ' ').trim().slice(0, 100) || null;

const getClickDestination = (element) => {
    const href = element.getAttribute('href');
    if (!href) return null;
    if (/^(mailto|tel):/i.test(href)) return href;
    try {
        const url = new URL(href, window.location.href);
        return url.origin === window.location.origin
            ? `${url.pathname}${url.hash}`
            : url.href;
    } catch {
        return href;
    }
};

const getClickSection = (element) => {
    let node = element.parentElement;
    while (node && node !== document.body) {
        if (node.id && !IGNORED_SECTION_IDS.has(node.id)) return node.id;
        if (['SECTION', 'HEADER', 'FOOTER', 'NAV'].includes(node.tagName)) {
            const heading = node.querySelector('h1, h2, h3');
            return (
                cleanText(heading?.textContent)?.slice(0, 60) ??
                node.tagName.toLowerCase()
            );
        }
        node = node.parentElement;
    }
    return null;
};

const pushCtaClick = ({ category, action, label } = {}, click = {}) => {
    pushToDataLayer('cta_click', {
        cta_name: action || null,
        cta_category: category || null,
        cta_label: label || null,
        cta_text: click.text ?? null,
        cta_destination: click.destination ?? null,
        cta_section: click.section ?? null,
        cta_type: click.type ?? null,
    });
};

// A same-tab navigation can unload the page before the deferred push runs.
const flushPendingClick = () => {
    if (!pendingClick) return;
    const click = pendingClick;
    pendingClick = null;
    pushCtaClick({}, click);
};

const onDocumentClick = (event) => {
    const element = event.target?.closest?.(CLICKABLE);
    if (!element) return;
    const click = {
        text: cleanText(
            element.textContent ||
                element.getAttribute('aria-label') ||
                element.getAttribute('title') ||
                element.querySelector('img')?.getAttribute('alt'),
        ),
        destination: getClickDestination(element),
        section: getClickSection(element),
        type: element.tagName === 'A' ? 'link' : 'button',
    };
    pendingClick = click;
    setTimeout(() => {
        if (pendingClick === click) {
            pendingClick = null;
            pushCtaClick({}, click);
        }
    }, 0);
};

export const initClickTracking = () => {
    if (typeof document === 'undefined') return;
    document.addEventListener('click', onDocumentClick, true);
    window.addEventListener('pagehide', flushPendingClick);
};

// Names the current click (`action` → `cta_name`). Called outside a click it
// still sends a `cta_click`, just without the element details.
export const trackCustomEvent = ({ category, action, label }) => {
    const click = pendingClick ?? {};
    pendingClick = null;
    pushCtaClick({ category, action, label }, click);
    return false;
};

// Lead submissions (GTM → GA4 key event + Google Ads conversion + Meta Lead).
// `email` feeds Enhanced Conversions; GTM hashes it before it leaves the page.
export const pushLeadEvent = (
    eventName,
    { formName, planTitle, selectedPlan, pdfVariant, email } = {},
) => {
    pushToDataLayer(eventName, {
        form_name: formName ?? eventName,
        form_location: getPageUrl(),
        plan_title: planTitle ?? null,
        selected_plan: selectedPlan ?? null,
        pdf_variant: pdfVariant ?? null,
        enhanced_conversion_data: email ? { email } : null,
    });
};

// Non-lead form and tool events (newsletter, job application, playgrounds).
// These and form_error share one GTM tag that reads all four keys, so each
// push sets all four — otherwise GTM's merged data model carries a value
// (e.g. an earlier error_type) into the next event.
export const pushFormEvent = (eventName, { formName, toolName } = {}) => {
    pushToDataLayer(eventName, {
        form_name: formName ?? null,
        form_location: getPageUrl(),
        tool_name: toolName ?? null,
        error_type: null,
    });
};

// `error`: 'validation' | 'recaptcha', or the error a submit request threw
// (a 400 is the backend rejecting the email, anything else is a server error).
export const trackFormError = (formName, error) => {
    const status = error?.status ?? error?.statusCode;
    const fromRequest = status === 400 ? 'invalid_email' : 'server';
    pushToDataLayer('form_error', {
        form_name: formName,
        form_location: getPageUrl(),
        error_type: typeof error === 'string' ? error : fromRequest,
        tool_name: null,
    });
};

export const onGetYourConsultancyClick = (gaConfig = {}) => {
    // TODO: TURNED OFF TEMPORARY
    return;
    // eslint-disable-next-line unicorn/no-abusive-eslint-disable,no-unreachable
    if (typeof window !== 'undefined') {
        trackCustomEvent({
            category: 'CTA button',
            action: 'Clicked scroll Request for free consultancy',
            ...gaConfig,
        });
    }
};

export const flattenMessages = (nestedMessages, prefix = '') => {
    if (nestedMessages === null) {
        return {};
    }
    return Object.keys(nestedMessages).reduce((messages, key) => {
        const value = nestedMessages[key];
        const prefixedKey = prefix ? `${prefix}.${key}` : key;

        if (typeof value === 'string') {
            Object.assign(messages, { [prefixedKey]: value });
        } else {
            Object.assign(messages, flattenMessages(value, prefixedKey));
        }

        return messages;
    }, {});
};

export const getCTAOrigin = () => {
    const fullPath = getPageUrl();
    const [, path] = fullPath.split('/').filter((chunk) => chunk !== '');

    return path;
};

export const getQuery = () => {
    if (typeof window !== 'undefined') {
        const url = window.location.search;
        const query = url.split('?')[1];
        return queryString.parse(query);
    }

    return {};
};

export const isEmptyObject = (object) => {
    return Object.entries(object).length === 0 && object.constructor === Object;
};

export const isNumberValue = (value) => /^\d*$/.test(value);

export const isValidEmail = (value) => {
    return !/^[\w%+.-]+@[\d.a-z-]+\.[a-z]{2,4}$/i.test(value);
};

export const isRequired = (value) =>
    value ? undefined : 'form-validation.required';

export const trackCustomEventGoogleAnalytics = (googleAnalytics) => {
    // Button passes an empty config by default; the click listener already
    // reports those clicks unnamed.
    if (!googleAnalytics?.action) {
        return;
    }
    trackCustomEvent(googleAnalytics);
};

export const setURLSearchParam = (key, value) => {
    const url = new URL(window.location.href);
    url.searchParams.set(key, value);
    window.history.pushState({ path: url.href }, '', url.href);
};

export const hasQueryParams = (uri, paramNames = []) => {
    try {
        const url = new URL(uri);
        const params = new URLSearchParams(url.search);

        if (paramNames.length === 0) {
            return params.toString().length > 0;
        }

        for (const paramName of paramNames) {
            if (!params.has(paramName)) {
                return false;
            }
        }

        return true;
    } catch {
        // eslint-disable-next-line no-console
        console.error(`${uri} is not a valid url`);
        return false;
    }
};

export const toastMessage = (message, type) => {
    const toastConfig = {
        duration: 3000,
        id: message,
    };
    if (type === 'error') {
        return toast.error(message, toastConfig);
    }
    return toast.success(message, toastConfig);
};

export const isGatsbyImage = (img) =>
    img && Object.keys(img).includes('childImageSharp');

export const checkBlackTheme = (url) =>
    BLACK_THEMED_PAGES.some((pageUrl) => {
        if (url.includes(pageUrl)) {
            return true;
        }
        return false;
    });

// Reads the Google Consent Mode cookies the banner writes. Vendor tags live in
// GTM now, so there are no per-vendor cookies (hotjar/linkedin/facebook) left
// to check — those were also never a reliable signal, since one of them was
// misspelled ("gpdr") and therefore never present.
export const areCookiesAccepted = () => {
    const analyticalCookies = Cookies.get(
        'gatsby-gdpr-google-analytics-storage',
    );
    const advertisingCookies =
        Cookies.get('gatsby-gdpr-google-ad-storage') &&
        Cookies.get('gatsby-gdpr-google-ad-user-data') &&
        Cookies.get('gatsby-gdpr-google-ad-personalization');

    return !!(analyticalCookies && advertisingCookies);
};

export const displayDate = (date) =>
    date ? date.split('-').reverse().join('.') : '';

export const isMessageTranslated = (messageId, formatMessage) => {
    if (!messageId) {
        return false;
    }

    return formatMessage({ id: messageId }) !== messageId;
};

export const handleCopyUrl = ({ formatMessage, kebabCaseId }) => {
    const currentUrl = `${window.location.origin}${window.location.pathname}#${kebabCaseId}`;
    navigator.clipboard.writeText(currentUrl).then(
        () => {
            toast.success(
                formatMessage({
                    id: 'general.copied',
                }),
                {
                    position: 'bottom-center',
                    duration: 1500,
                },
            );
        },
        () => {
            toast.error(
                formatMessage({
                    id: 'general.copyFailed',
                }),
                {
                    position: 'bottom-center',
                    duration: 1500,
                },
            );
        },
    );
};

export const isMobile = () => {
    return typeof window !== 'undefined' && window.innerWidth < 1024;
};
